import { marked } from "marked";
import DOMPurify from "dompurify";

marked.setOptions({ gfm: true, breaks: true });

// One sanitiser, one configuration, two entry points below. Lifted to a
// constant so renderMarkdown and renderUntrustedHtml cannot drift into two
// different security postures.
const SANITIZE_OPTS = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ["style", "form", "input", "iframe", "object", "embed"],
  FORBID_ATTR: ["style", "onerror", "onload", "onclick"],
};

/**
 * The ONLY place author-supplied Markdown becomes HTML in this app -- see
 * `renderUntrustedHtml` below for the sibling path that sanitises HTML which
 * arrived already-formed (never run through `marked`).
 *
 * Every caller feeds the result to dangerouslySetInnerHTML, so every caller
 * depends on this function sanitising. Do not add a second render path, and do
 * not "optimise" the sanitise step away because the input is internal: a KB
 * article is written by one technician and rendered in another's browser, which
 * is precisely the stored-XSS shape.
 */
export function renderMarkdown(md) {
  if (!md) return "";
  return DOMPurify.sanitize(marked.parse(String(md)), SANITIZE_OPTS);
}

/**
 * The other sanitised render path: HTML that already IS HTML -- an inbound
 * email body, the most hostile input this app renders -- never markdown.
 *
 * Deliberately skips `marked.parse()`: markdown-parsing real HTML mangles
 * it, and this module's `breaks: true` would turn every bare newline in a
 * plain-text-formatted mail into a stray `<br>`. DOMPurify still runs, with
 * the exact same `SANITIZE_OPTS` as `renderMarkdown`, so this path is not a
 * second sanitiser with its own opinion -- it is the one sanitiser, called
 * without the markdown step first.
 */
export function renderUntrustedHtml(html) {
  if (!html) return "";
  DOMPurify.addHook("afterSanitizeAttributes", _blockRemoteContent);
  try {
    return DOMPurify.sanitize(String(html), SANITIZE_OPTS);
  } finally {
    // Removed in a finally so a throw cannot leave the hook installed and
    // silently start stripping images out of KB articles too.
    DOMPurify.removeHook("afterSanitizeAttributes");
  }
}

/**
 * Stop a quarantined message phoning home the moment it is displayed.
 *
 * DOMPurify's job is script execution, and it does that correctly -- but it
 * KEEPS `<img src>` and `<a href>`, which is right for a sanitiser and wrong
 * for this screen. The app ships no Content-Security-Policy at any layer
 * (checked: no meta tag, nothing in the nginx vhost), so nothing else
 * constrains them either.
 *
 * The consequence is the ordinary tracking-pixel one, and it needs no click:
 * opening Mail Quarantine loads a remote image per pending row, which tells
 * every one of those senders that the address is live, monitored, and read
 * at this moment -- and hands them the technician's IP and user agent. These
 * are by definition messages from people the system does not recognise,
 * which is exactly the population that should not get a read receipt. Every
 * mail client blocks remote content by default for this reason.
 *
 * So: `src` is dropped and the element is marked, rather than the image
 * being removed, because "there was a picture here and we did not fetch it"
 * is information a triager wants. Links KEEP their href -- seeing where a
 * phishing link points is the point of triage -- but lose `target` and gain
 * `rel="noopener noreferrer"`, so following one deliberately does not leak a
 * referrer or hand over `window.opener`.
 */
function _blockRemoteContent(node) {
  const tag = node.tagName && node.tagName.toLowerCase();
  if (tag === "img" || tag === "source") {
    for (const attr of ["src", "srcset", "data-src"]) {
      if (node.hasAttribute && node.hasAttribute(attr)) node.removeAttribute(attr);
    }
    if (node.setAttribute) {
      node.setAttribute("data-remote-blocked", "true");
      if (!node.getAttribute("alt")) node.setAttribute("alt", "[remote image not loaded]");
    }
  }
  if (tag === "a" && node.setAttribute) {
    node.setAttribute("rel", "noopener noreferrer");
    if (node.hasAttribute("target")) node.removeAttribute("target");
  }
}
